import { useMemo } from 'react';

import type { CurrentPageContextType } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/types';
import { useDocumentTitle } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/useDocumentTitle';
import { useWindowLocation } from '../../../common/ui/chat-input-action-buttons/current-page-as-context/useWindowLocation';
import { type CurrentEntityContext } from '../../../types';

import type { ContextBannerSmartLinkInfo } from './ContextBannerSmartLinkInfo';
import { getContextBannerSmartLinkInfo } from './getContextBannerSmartLinkInfo';

export const useContextBannerSmartLinkInfo = ({
	currentContext,
	hasBrowserContext,
	browserContextUrl,
}: {
	currentContext: CurrentPageContextType | CurrentEntityContext;
	hasBrowserContext: boolean;
	browserContextUrl: string | undefined;
}): ContextBannerSmartLinkInfo => {
	// Subscribe to location changes so the banner re-derives its URL on SPA
	// navigation even when the parent does not re-render (e.g. when this
	// component is memoized). A raw `window.location.href` read here would be
	// a render-time global read with no subscription, so under React.memo it
	// could go stale on pushState/replaceState/popstate navigations.
	const windowHref = useWindowLocation();
	// `document.title` is updated by the shell *after* the navigation that changes
	// the location, so reading it reactively (rather than at render time) is what
	// keeps the smart-link placeholder from lagging one navigation behind.
	const documentTitle = useDocumentTitle();

	return useMemo(
		() =>
			getContextBannerSmartLinkInfo({
				currentContext,
				hasBrowserContext,
				browserContextUrl,
				windowHref,
				documentTitle,
			}),
		[currentContext, hasBrowserContext, browserContextUrl, windowHref, documentTitle],
	);
};
